import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const UserAcquisition = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetchWithAuth('/api/analytics/user-acquisition');
      if (!response.ok) throw new Error('Failed to fetch acquisition data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading user acquisition data...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return null;

  const { sources, topUsersBySource, summary } = data;

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
        🎯 User Acquisition Analysis
      </h1>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '16px' }}>
        Where users come from and their food logging engagement
      </p>

      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <div className="stat-card" style={{ background: '#eff6ff' }}>
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{summary.totalUsers.toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            Across all sources
          </div>
        </div>

        <div className="stat-card" style={{ background: '#f0fdf4' }}>
          <div className="stat-label">Total Food Logs (2025)</div>
          <div className="stat-value">{summary.totalFoodLogs.toLocaleString()}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {Math.round(summary.totalFoodLogs / summary.totalUsers)} avg per user
          </div>
        </div>

        <div className="stat-card" style={{ background: '#fef3c7' }}>
          <div className="stat-label">Best by Engagement</div>
          <div className="stat-value" style={{ fontSize: '20px' }}>
            {summary.bestSourceByEngagement.source_name}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {Math.round(summary.bestSourceByEngagement.median_food_logs)} median logs
          </div>
        </div>

        <div className="stat-card" style={{ background: '#f3e8ff' }}>
          <div className="stat-label">Best by Volume</div>
          <div className="stat-value" style={{ fontSize: '20px' }}>
            {summary.bestSourceByVolume.source_name}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {summary.bestSourceByVolume.user_count.toLocaleString()} users
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div className="card" style={{ 
        background: '#dbeafe', 
        border: '2px solid #3b82f6',
        marginBottom: '24px' 
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px' }}>
          💡 Key Insight
        </h2>
        <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#1e40af' }}>
          <strong>{summary.bestSourceByEngagement.source_name}</strong> brings the most engaged users 
          (median: <strong>{Math.round(summary.bestSourceByEngagement.median_food_logs)} food logs</strong>), 
          while <strong>{summary.bestSourceByVolume.source_name}</strong> brings the most users overall 
          (<strong>{summary.bestSourceByVolume.user_count}</strong> users).
          {summary.bestSourceByEngagement.hear_about_us !== summary.bestSourceByVolume.hear_about_us && (
            <span> Consider investing more in {summary.bestSourceByEngagement.source_name} for quality users!</span>
          )}
        </p>
      </div>

      {/* Acquisition Sources Table */}
      <div className="card">
        <h2 className="card-title">📊 Acquisition Sources Performance</h2>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
          Comparing user volume and engagement across different sources
        </p>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th>Users</th>
                <th>Total Logs (2025)</th>
                <th>Avg Logs/User</th>
                <th>Median Logs/User</th>
                <th>Total Calories</th>
                <th>Quality Score</th>
              </tr>
            </thead>
            <tbody>
              {sources
                .sort((a, b) => b.median_food_logs - a.median_food_logs)
                .map((source, index) => {
                  const qualityScore = source.median_food_logs > 0 
                    ? Math.min(100, Math.round((source.median_food_logs / 100) * 100))
                    : 0;
                  
                  return (
                    <tr key={source.hear_about_us}>
                      <td>
                        <strong style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : ''}
                          {source.source_name}
                        </strong>
                      </td>
                      <td>{source.user_count.toLocaleString()}</td>
                      <td>{source.total_food_logs.toLocaleString()}</td>
                      <td>{source.avg_food_logs || 0}</td>
                      <td>
                        <strong style={{ color: '#3b82f6' }}>
                          {Math.round(source.median_food_logs)}
                        </strong>
                      </td>
                      <td>{source.total_calories ? Math.round(source.total_calories).toLocaleString() : 0} kcal</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '60px',
                            height: '6px',
                            background: '#e5e7eb',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${qualityScore}%`,
                              height: '100%',
                              background: qualityScore > 70 ? '#10b981' : qualityScore > 40 ? '#f59e0b' : '#ef4444',
                              transition: 'width 0.3s'
                            }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {qualityScore}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Users per Source */}
      <div style={{ marginTop: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px' }}>
          🌟 Top Users by Source
        </h2>
        <div className="grid grid-2">
          {sources
            .filter(s => topUsersBySource[s.hear_about_us] && topUsersBySource[s.hear_about_us].length > 0)
            .sort((a, b) => b.median_food_logs - a.median_food_logs)
            .slice(0, 6)
            .map(source => (
              <div key={source.hear_about_us} className="card">
                <h3 className="card-title">{source.source_name}</h3>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                  Median: <strong>{Math.round(source.median_food_logs)} logs</strong> | 
                  Total users: <strong>{source.user_count}</strong>
                </div>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '8px 0', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '8px 0', textAlign: 'right' }}>Logs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topUsersBySource[source.hear_about_us].slice(0, 5).map((user, idx) => (
                      <tr key={user.user_id}>
                        <td style={{ padding: '6px 0' }}>
                          {idx + 1}. {user.name || user.email || `User ${user.user_id}`}
                        </td>
                        <td style={{ padding: '6px 0', textAlign: 'right' }}>
                          <strong>{user.food_count}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="card" style={{ marginTop: '24px', background: '#f9fafb' }}>
        <h2 className="card-title">🎯 Marketing Recommendations</h2>
        <div style={{ lineHeight: '1.8', fontSize: '14px' }}>
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#10b981' }}>✅ Double Down:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
              <li>Invest more in <strong>{summary.bestSourceByEngagement.source_name}</strong> - highest engagement (median: {Math.round(summary.bestSourceByEngagement.median_food_logs)} logs)</li>
              <li>These users are most likely to become long-term active users</li>
            </ul>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ color: '#3b82f6' }}>📈 Optimize:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
              <li><strong>{summary.bestSourceByVolume.source_name}</strong> brings volume - improve onboarding to boost engagement</li>
              <li>Focus on converting these users from casual to active</li>
            </ul>
          </div>
          
          <div>
            <strong style={{ color: '#f59e0b' }}>⚠️ Review:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
              <li>Sources with low median logs may need better targeting or onboarding</li>
              <li>Consider A/B testing different user journeys per source</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAcquisition;


